/**
 * Shared error handling utilities
 * Provides consistent error extraction and logging across the application
 */

/**
 * Standard API error envelope: { "status":"error", "error": { "code", "message" } }
 * Legacy shapes (detail, top-level message, string error) remain supported.
 */
export const extractMessageFromResponseData = (
  data: unknown,
): string | undefined => {
  if (typeof data === "string") {
    const trimmed = data.trim();
    return trimmed || undefined;
  }

  if (!data || typeof data !== "object") {
    return undefined;
  }

  const d = data as Record<string, unknown>;

  // New envelope: error is an object with code/message (not a legacy string)
  const nested = d.error;
  if (
    nested &&
    typeof nested === "object" &&
    nested !== null &&
    !Array.isArray(nested)
  ) {
    const errObj = nested as Record<string, unknown>;
    if (typeof errObj.message === "string" && errObj.message) {
      return errObj.message;
    }
  }

  // Legacy field order preserved: detail, message, then string error
  if (typeof d.detail === "string" && d.detail) {
    return d.detail;
  }
  if (typeof d.message === "string" && d.message) {
    return d.message;
  }
  if (typeof d.error === "string" && d.error) {
    return d.error;
  }

  return undefined;
};

/**
 * With `responseType: "blob"`, failed requests still receive the body as a Blob.
 * Parse JSON error envelopes (e.g. `{ status, error: { code, message } }`) when possible.
 */
export const parseBlobOrJsonResponseData = async (
  data: unknown,
): Promise<unknown> => {
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const trimmed = text.trim();
      if (!trimmed) {
        return undefined;
      }
      return JSON.parse(trimmed) as unknown;
    } catch {
      return undefined;
    }
  }
  return data;
};

/**
 * Interface for error messages configuration
 */
export interface ErrorMessageConfig {
  defaultMessage: string;
  /** When HTTP status is 500 and set, use this instead of parsing the response body */
  status500Message?: string;
}

/**
 * Extract error message from API response
 * Shows backend error message whenever available
 * @param error - The error object (Axios error, Error, or unknown)
 * @param config - Configuration for error messages
 * @returns Error message string
 */
export const extractErrorMessage = (
  error: unknown,
  config: ErrorMessageConfig,
): string => {
  // Check if it's an Axios error with response
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as {
      response?: {
        data?: unknown;
        status?: number;
      };
    };

    const status = axiosError.response?.status;
    if (status === 500 && config.status500Message) {
      return config.status500Message;
    }

    const responseData = axiosError.response?.data;

    const errorMessage = extractMessageFromResponseData(responseData);

    if (errorMessage) {
      return errorMessage;
    }
  }

  // Check if error has a message property (e.g., Error object)
  if (error instanceof Error && error.message) {
    // If it's a generic axios error message, try to extract from response
    if (error.message.includes("status code")) {
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: {
            data?: unknown;
            status?: number;
          };
        };
        const status = axiosError.response?.status;
        if (status === 500 && config.status500Message) {
          return config.status500Message;
        }
        const responseData = axiosError.response?.data;
        const nestedMessage = extractMessageFromResponseData(responseData);
        if (nestedMessage) {
          return nestedMessage;
        }
      }
    }
  }

  // Default generic error message for all other cases
  return config.defaultMessage;
};

/**
 * Safe error logger that only logs in development
 * Prevents sensitive information from being exposed in production
 * @param context - Context where the error occurred
 * @param error - The error object
 * @param additionalInfo - Optional additional information to log
 */
export const safeErrorLog = (
  context: string,
  error: unknown,
  additionalInfo?: Record<string, unknown>,
): void => {
  // Only log in development environment
  if (import.meta.env.DEV) {
    const errorInfo: Record<string, unknown> = {
      context,
      timestamp: new Date().toISOString(),
      ...additionalInfo,
    };

    if (error instanceof Error) {
      errorInfo.errorMessage = error.message;
      errorInfo.errorName = error.name;
      // Only include stack trace in development
      if (error.stack) {
        errorInfo.errorStack = error.stack;
      }
    } else if (error && typeof error === "object") {
      // For Axios errors, only log safe information
      if ("response" in error) {
        const axiosError = error as {
          response?: {
            status?: number;
            statusText?: string;
          };
        };
        errorInfo.status = axiosError.response?.status;
        errorInfo.statusText = axiosError.response?.statusText;
      }
    }

    console.error(`[${context}]`, errorInfo);
  }
};
